import { useRef, useCallback, useEffect, useState } from "react";
import { useQueryClient } from "react-query";

import {
  useAdminState,
  ADMIN_USER_TYPES,
  ADMIN_MOVEIN_STEPS,
  ADMIN_CHECKING_STEPS,
  ADMIN_DD_STEPS,
  ADMIN_CREDIT_BUILDER,
  ADMIN_CASHBACK_RENT,
  ADMIN_SPEND_TRACKER,
  ADMIN_CONNECTED_ACCOUNTS,
  ADMIN_RECURRING_DEPOSITS,
  ADMIN_STAKE_DEBIT,
  ADMIN_INSTANT_CASH_OUT,
  ADMIN_STAKE_SAVER,
} from "../../Contexts/AdminStateContext";

import { CoreAPIRequest } from "../../Utils/web-api-handler";
import { dispatchSuccessToast, dispatchErrorToast, getUserId, getUserEmail } from "../../Utils/helpers";

import "./admin-state-panel.scss";

function AdminColumn({ title, icon, count, countLabel, items, selected, onSelect, colorClass, disabled, onApiPatch, patchingField }) {
  return (
    <div className="admin-flow">
      <div className={`adm-flow-header ${colorClass}`}>
        {icon}
        <span className="adm-flow-title">{title}</span>
        <span className="adm-flow-count">
          {count} {countLabel}
        </span>
      </div>
      <div className="adm-col-body">
        {items.map((s) => {
          const isPatching = s.apiField && patchingField === `${s.apiField}:${s.apiValue}`;
          return (
            <div
              key={s.state}
              className={`adm-step${selected === s.state ? " active" : ""}${disabled ? " disabled" : ""}${isPatching ? " patching" : ""}`}
              onClick={disabled ? undefined : () => {
                if (s.apiField && onApiPatch) {
                  onApiPatch(s.apiField, s.apiValue, s.state);
                } else {
                  onSelect(s.state);
                }
              }}
            >
              <div className="adm-step-dot" />
              <div className="adm-step-info">
                <span className="adm-step-name">{isPatching ? "Updating…" : s.name}</span>
                <span className="adm-step-desc">{s.desc}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminStatePanel() {
  const {
    mode,
    setMode,
    panelOpen,
    togglePanel,
    panelHeight,
    setPanelHeight,
    selUserType,
    setSelUserType,
    selMovein,
    setSelMovein,
    selChecking,
    setSelChecking,
    selDirectDeposit,
    setSelDirectDeposit,
    selCreditBuilder,
    setSelCreditBuilder,
    selCashback,
    setSelCashback,
    selSpend,
    setSelSpend,
    selConnectedAccounts,
    setSelConnectedAccounts,
    selRecurringDeposits,
    setSelRecurringDeposits,
    selStakeDebit,
    setSelStakeDebit,
    selInstantCashOut,
    setSelInstantCashOut,
    selStakeSaver,
    setSelStakeSaver,
    zoomLevel,
    setZoomLevel,
  } = useAdminState();

  const adminRef = useRef(null);
  const adminDragRef = useRef({ dragging: false, startY: 0, startH: 0 });

  const queryClient = useQueryClient();

  const isLive = mode === "live";
  const disabled = isLive;

  const [patchingField, setPatchingField] = useState(null);

  const patchTestUser = useCallback(async (fieldName, value) => {
    const key = `${fieldName}:${value}`;
    setPatchingField(key);
    try {
      const tokenRes = await CoreAPIRequest.get("/mobile/get_transfer_token/", {
        headers: { userId: getUserId() },
      });
      const transferToken = tokenRes.data?.data?.token;
      if (!transferToken) throw new Error("No transfer token returned");

      const formData = new FormData();
      formData.append(fieldName, value);
      await CoreAPIRequest.patch(`/api/resident/?email=${encodeURIComponent(getUserEmail())}`, formData, {
        headers: { auth: transferToken },
      });
      setMode("live");
      await queryClient.refetchQueries(["deals"]);
      await queryClient.refetchQueries(["credit-reporting"]);
      await queryClient.refetchQueries(["consumer-deals"]);
      dispatchSuccessToast(`${fieldName} → ${value}`);
      return true;
    } catch (err) {
      dispatchErrorToast(err?.response?.data?.message || `Failed to update ${fieldName}`);
      return false;
    } finally {
      setPatchingField(null);
    }
  }, []);

  const handleApiPatch = useCallback(async (field, value, stepState, selectFn) => {
    const success = await patchTestUser(field, value);
    if (success) selectFn(stepState);
  }, [patchTestUser]);

  useEffect(() => {
    const el = adminRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty("--admin-panel-h", `${entry.contentRect.height}px`);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty("--admin-panel-h");
    };
  }, []);

  const onAdminDragStart = useCallback(
    (e) => {
      e.preventDefault();
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const rect = adminRef.current?.getBoundingClientRect();
      if (!rect) return;
      adminDragRef.current = { dragging: true, startY: clientY, startH: rect.height };
      const onMove = (ev) => {
        if (!adminDragRef.current.dragging) return;
        const cy = ev.touches ? ev.touches[0].clientY : ev.clientY;
        const delta = adminDragRef.current.startY - cy;
        const newH = Math.min(window.innerHeight * 0.8, Math.max(120, adminDragRef.current.startH + delta));
        setPanelHeight(newH);
      };
      const onEnd = () => {
        adminDragRef.current.dragging = false;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onEnd);
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onEnd);
      document.addEventListener("touchmove", onMove);
      document.addEventListener("touchend", onEnd);
    },
    [setPanelHeight],
  );

  const userTypeIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3 16c0-3.3 2.7-6 6-6s6 2.7 6 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
  const moveinIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 3v12M5 7l4-4 4 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const checkingIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="4" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 8h14" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="5" cy="11" r="1" fill="currentColor" />
    </svg>
  );
  const ddIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.3" />
      <path d="M9 6v6M6 9h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
  const creditBuilderIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M4 14V8l5-4 5 4v6a1 1 0 01-1 1H5a1 1 0 01-1-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M7 15v-4h4v4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
  const cashbackIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="11" width="3" height="5" rx="0.5" fill="currentColor" opacity="0.3" />
      <rect x="6.5" y="8" width="3" height="8" rx="0.5" fill="currentColor" opacity="0.5" />
      <rect x="11" y="5" width="3" height="11" rx="0.5" fill="currentColor" opacity="0.8" />
      <path d="M3 10L8 6l4 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const spendIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 2l1.8 4h4.2l-3.4 2.5 1.3 4.5L9 10.5 5.1 13l1.3-4.5L3 6h4.2L9 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
  const connectedIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M7.5 10.5a3 3 0 004.24 0l2.25-2.25a3 3 0 00-4.24-4.24l-1.13 1.12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.5 7.5a3 3 0 00-4.24 0L4.01 9.75a3 3 0 004.24 4.24l1.12-1.12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const stakeDebitIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2" y="4" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M2 8h14" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5 12h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
  const instantCashOutIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M10 2L5 10h4l-1 6 5-8h-4l1-6z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const stakeSaverIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 1.5L3 4.5v4.5c0 4.14 2.56 8.01 6 9 3.44-.99 6-4.86 6-9V4.5L9 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M6.5 9l2 2 3.5-3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const recurringIcon = (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.25 9a6.75 6.75 0 0111.52-4.77L15.75 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.75 2.25V6h-3.75" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15.75 9a6.75 6.75 0 01-11.52 4.77L2.25 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.25 15.75V12h3.75" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <div
      className={`admin-panel${panelOpen ? " open" : ""}`}
      ref={adminRef}
      style={panelOpen && panelHeight ? { height: panelHeight } : undefined}
    >
      {panelOpen && (
        <div className="adm-resize-handle" onMouseDown={onAdminDragStart} onTouchStart={onAdminDragStart} />
      )}
      <div className="admin-panel-header">
        <div className="admin-left">
          <div className="admin-dot" />
          <span className="admin-label">Admin</span>
          <span className="admin-sublabel">User State Matrix</span>
        </div>
        <div className="admin-right">
          {panelOpen && (
            <div className="admin-mode-toggle">
              <button
                className={`mode-btn${isLive ? " active" : ""}`}
                onClick={() => setMode("live")}
              >
                Live Data
              </button>
              <button
                className={`mode-btn${!isLive ? " active" : ""}`}
                onClick={() => setMode("mock")}
              >
                Mock Data
              </button>
            </div>
          )}
          {panelOpen && (
            <>
              <span className="admin-hint">Select a step to preview the returning user experience at that drop-off point</span>
              <div className="admin-zoom-controls">
                <button
                  className="zoom-btn"
                  onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.1))}
                  aria-label="Zoom out"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
                    <path d="M4 6h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
                <span className="zoom-label">{Math.round(zoomLevel * 100)}%</span>
                <button
                  className="zoom-btn"
                  onClick={() => setZoomLevel((z) => Math.min(1.5, z + 0.1))}
                  aria-label="Zoom in"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.2" />
                    <path d="M4 6h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M6 4v4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    <path d="M9.5 9.5L12.5 12.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            </>
          )}
          <div className="admin-toggle" onClick={togglePanel}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              style={{ transform: panelOpen ? "" : "rotate(180deg)", transition: "transform 200ms" }}
            >
              <path
                d="M4.5 10l3.5-3.5L11.5 10"
                stroke="#6C7086"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
      {panelOpen && (
        <div
          className={`admin-states${disabled ? " disabled-columns" : ""}`}
          style={{ '--zoom': zoomLevel }}
        >
          <AdminColumn
            title="User Type"
            icon={userTypeIcon}
            count={ADMIN_USER_TYPES.length}
            countLabel="types"
            items={ADMIN_USER_TYPES}
            selected={selUserType}
            onSelect={setSelUserType}
            colorClass="purple"
            disabled={disabled}
          />
          <AdminColumn
            title="Move-in Cash"
            icon={moveinIcon}
            count={ADMIN_MOVEIN_STEPS.length}
            countLabel="states"
            items={ADMIN_MOVEIN_STEPS}
            selected={selMovein}
            onSelect={setSelMovein}
            colorClass="amber"
            disabled={disabled}
          />
          <AdminColumn
            title="Stake Checking"
            icon={checkingIcon}
            count={ADMIN_CHECKING_STEPS.length}
            countLabel="steps"
            items={ADMIN_CHECKING_STEPS}
            selected={selChecking}
            onSelect={setSelChecking}
            colorClass="green"
            disabled={disabled}
          />
          <AdminColumn
            title="Direct Deposit"
            icon={ddIcon}
            count={ADMIN_DD_STEPS.length}
            countLabel="steps"
            items={ADMIN_DD_STEPS}
            selected={selDirectDeposit}
            onSelect={setSelDirectDeposit}
            colorClass="blue"
            disabled={disabled}
          />
          <AdminColumn
            title="Credit Builder"
            icon={creditBuilderIcon}
            count={ADMIN_CREDIT_BUILDER.length}
            countLabel="options"
            items={ADMIN_CREDIT_BUILDER}
            selected={selCreditBuilder}
            onSelect={setSelCreditBuilder}
            colorClass="teal"
            disabled={disabled}
            onApiPatch={(field, value, state) => handleApiPatch(field, value, state, setSelCreditBuilder)}
            patchingField={patchingField}
          />
          <AdminColumn
            title="Cash Back on Rent"
            icon={cashbackIcon}
            count={ADMIN_CASHBACK_RENT.length}
            countLabel="states"
            items={ADMIN_CASHBACK_RENT}
            selected={selCashback}
            onSelect={setSelCashback}
            colorClass="green"
            disabled={disabled}
          />
          <AdminColumn
            title="Spend Tracker"
            icon={spendIcon}
            count={ADMIN_SPEND_TRACKER.length}
            countLabel="states"
            items={ADMIN_SPEND_TRACKER}
            selected={selSpend}
            onSelect={setSelSpend}
            colorClass="green"
            disabled={disabled}
          />
          <AdminColumn
            title="Connected Accounts"
            icon={connectedIcon}
            count={ADMIN_CONNECTED_ACCOUNTS.length}
            countLabel="states"
            items={ADMIN_CONNECTED_ACCOUNTS}
            selected={selConnectedAccounts}
            onSelect={setSelConnectedAccounts}
            colorClass="green"
            disabled={disabled}
          />
          <AdminColumn
            title="Recurring Deposits"
            icon={recurringIcon}
            count={ADMIN_RECURRING_DEPOSITS.length}
            countLabel="states"
            items={ADMIN_RECURRING_DEPOSITS}
            selected={selRecurringDeposits}
            onSelect={setSelRecurringDeposits}
            colorClass="green"
            disabled={disabled}
          />
          <AdminColumn
            title="Stake Debit"
            icon={stakeDebitIcon}
            count={ADMIN_STAKE_DEBIT.length}
            countLabel="states"
            items={ADMIN_STAKE_DEBIT}
            selected={selStakeDebit}
            onSelect={setSelStakeDebit}
            colorClass="green"
            disabled={disabled}
            onApiPatch={(field, value, state) => handleApiPatch(field, value, state, setSelStakeDebit)}
            patchingField={patchingField}
          />
          <AdminColumn
            title="Instant Transfers (Astra)"
            icon={instantCashOutIcon}
            count={ADMIN_INSTANT_CASH_OUT.length}
            countLabel="states"
            items={ADMIN_INSTANT_CASH_OUT}
            selected={selInstantCashOut}
            onSelect={setSelInstantCashOut}
            colorClass="amber"
            disabled={disabled}
            onApiPatch={(field, value, state) => handleApiPatch(field, value, state, setSelInstantCashOut)}
            patchingField={patchingField}
          />
          <AdminColumn
            title="Stake Saver"
            icon={stakeSaverIcon}
            count={ADMIN_STAKE_SAVER.length}
            countLabel="states"
            items={ADMIN_STAKE_SAVER}
            selected={selStakeSaver}
            onSelect={setSelStakeSaver}
            colorClass="green"
            disabled={disabled}
            onApiPatch={(field, value, state) => handleApiPatch(field, value, state, setSelStakeSaver)}
            patchingField={patchingField}
          />
        </div>
      )}
    </div>
  );
}
